import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import Swal from 'sweetalert2';
import { AutoRefillService } from '../../../services/auto-refill.service';
import { SharedService } from '../../../services/shared.service';
import * as FileSaver from 'file-saver';
import { AUTO_REFILL_PHARMACY_OPTIONS } from './auto-refill.constants';
import {
  buildNursingHomeOptions,
  formatFileStamp,
  isScheduleUrgent,
  hasScheduleNote,
  pickDto,
  scheduleNoteText
} from './auto-refill.utils';

@Component({
  selector: 'app-auto-refill-todo-tab',
  templateUrl: './auto-refill-todo-tab.component.html',
  styleUrls: ['./auto-refill.component.css']
})
export class AutoRefillTodoTabComponent implements OnInit, OnChanges {

  @Input() selectedPharmacyId = 0;
  @Output() selectedPharmacyIdChange = new EventEmitter<number>();
  @Output() checkRow = new EventEmitter<any>();
  @Output() editRow = new EventEmitter<any>();
  @Output() history = new EventEmitter<any>();
  @Output() deactivate = new EventEmitter<any>();
  @Output() error = new EventEmitter<string>();

  readonly pharmacyOptions = AUTO_REFILL_PHARMACY_OPTIONS;

  daysAhead = 7;
  nursingHomeFilter = 'All';
  nursingHomeOptions: string[] = ['All'];
  todoRows: any[] = [];
  todoLoading = false;
  todoLoaded = false;
  currentSortField = 'residentName';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private autoRefillService: AutoRefillService,
    private sharedService: SharedService
  ) { }

  ngOnInit(): void {
    var self = this;
    this.sharedService.reloadUserPrivileges().then(function () {
      self.loadTodoList();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedPharmacyId && !changes.selectedPharmacyId.firstChange) {
      this.nursingHomeFilter = 'All';
      this.loadTodoList();
    }
  }

  pick = pickDto;
  isUrgent = isScheduleUrgent;
  hasNote = hasScheduleNote;
  scheduleNote = scheduleNoteText;

  loadTodoList(): void {
    var userId = this.sharedService.getUserId();
    if (!userId || this.selectedPharmacyId <= 0) {
      return;
    }
    this.todoLoading = true;
    this.todoLoaded = false;
    var self = this;
    var nh = this.nursingHomeFilter === 'All' ? '' : this.nursingHomeFilter;
    this.autoRefillService.getTodoList(userId, this.selectedPharmacyId, this.daysAhead, nh).subscribe(
      function (data) {
        self.todoRows = data || [];
        self.todoLoading = false;
        self.todoLoaded = true;
        self.nursingHomeOptions = buildNursingHomeOptions(self.todoRows);
        self.currentSortField = 'residentName';
        self.sortDirection = 'asc';
        self.applySort();
        if (self.todoRows.length === 0) {
          Swal.fire({
            icon: 'info',
            title: 'No pending refills',
            text: 'No pending refills for the next ' + self.daysAhead + ' days for this selection.',
            confirmButtonText: 'OK'
          });
        }
      },
      function () {
        self.todoLoading = false;
        self.todoLoaded = true;
        self.error.emit('Could not load the to-do list. Check database tables and Kroll connection.');
      }
    );
  }

  onSort(field: string): void {
    if (!field) {
      return;
    }
    if (this.currentSortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortField = field;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  applySort(): void {
    if (!this.todoRows || this.todoRows.length === 0) {
      return;
    }
    var self = this;
    var field = this.currentSortField;
    var ascending = this.sortDirection === 'asc';
    this.todoRows = this.todoRows.slice().sort(function (a, b) {
      var av = self.getSortValue(a, field);
      var bv = self.getSortValue(b, field);
      if (av < bv) {
        return ascending ? -1 : 1;
      }
      if (av > bv) {
        return ascending ? 1 : -1;
      }
      // Stable secondary: patient name then due date
      var an = self.getSortValue(a, 'residentName');
      var bn = self.getSortValue(b, 'residentName');
      if (an < bn) {
        return -1;
      }
      if (an > bn) {
        return 1;
      }
      var ad = self.getSortValue(a, 'nextFill');
      var bd = self.getSortValue(b, 'nextFill');
      if (ad < bd) {
        return -1;
      }
      if (ad > bd) {
        return 1;
      }
      return 0;
    });
  }

  private getSortValue(row: any, field: string): string | number {
    if (field === 'scheduleDays') {
      var days = pickDto(row, 'scheduleDays', 'ScheduleDays');
      var n = Number(days);
      return isNaN(n) ? 0 : n;
    }
    if (field === 'nextFill') {
      var raw = pickDto(row, 'nextFillRaw', 'NextFillRaw');
      if (raw) {
        return String(raw).toLowerCase();
      }
    }
    var map: { [key: string]: string[] } = {
      residentName: ['residentName', 'ResidentName'],
      nursingHome: ['nursingHome', 'NursingHome'],
      brandName: ['brandName', 'BrandName'],
      scheduleFreq: ['scheduleFreq', 'ScheduleFreq'],
      firstFill: ['firstFill', 'FirstFill'],
      lastFill: ['lastFill', 'LastFill'],
      nextFill: ['nextFill', 'NextFill'],
      notes: ['notes', 'Notes']
    };
    var keys = map[field];
    var value = keys ? pickDto(row, keys[0], keys[1]) : '';
    return value == null ? '' : String(value).toLowerCase();
  }

  onPharmacyChange(): void {
    this.selectedPharmacyIdChange.emit(this.selectedPharmacyId);
    this.nursingHomeFilter = 'All';
    this.loadTodoList();
  }

  onDaysOrNhChange(): void {
    this.loadTodoList();
  }

  exportPdf(): void {
    if (!this.todoRows || this.todoRows.length === 0) {
      return;
    }

    var contentEl = document.getElementById('auto-refill-print-content');
    if (!contentEl) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to export',
        text: 'Print content was not found on the page.',
        confirmButtonText: 'OK'
      });
      return;
    }

    var printWindow = window.open('', '_blank');
    if (!printWindow) {
      Swal.fire({
        icon: 'warning',
        title: 'Popup blocked',
        text: 'Please allow popups to export the list.',
        confirmButtonText: 'OK'
      });
      return;
    }

    var title = 'AutoRefills_' + formatFileStamp(new Date());
    var cloned = contentEl.cloneNode(true) as HTMLElement;
    var contentHtml = cloned.outerHTML;

    var subtitle = this.nursingHomeFilter === 'All' ? 'All Facilities' : this.nursingHomeFilter;
    subtitle += ' | Next ' + this.daysAhead + ' day(s)';

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${title}</title>
          <style>
            @page { margin: 0; }
            body { font-family: Arial, Helvetica, sans-serif; color: #212529; margin: 1.5cm; }
            h2 { margin-bottom: 5px; font-size: 1.25rem; }
            h4 { margin-top: 0; margin-bottom: 20px; font-size: 0.9rem; color: #6c757d; font-weight: normal; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 0.8rem; }
            th, td { border: 1px solid #dee2e6; padding: 6px; text-align: left; }
            th {
              background-color: #e9ecef !important;
              font-weight: bold;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .misc-ar-action-cell, th:nth-child(9) { display: none !important; }
            .misc-ar-col-first-fill { display: none !important; }
            .misc-ar-sortable i, [app-sorted-arrow] { display: none !important; }
            .ar-urgent { color: #dc3545; font-weight: bold; }
            .misc-ar-note-text { white-space: pre-wrap; font-size: 0.75rem; }
          </style>
        </head>
        <body>
          <h2 style="text-align: center;">Auto Refills To-Do List</h2>
          <h4 style="text-align: center;">${subtitle}</h4>
          ${contentHtml}
        </body>
      </html>
    `);
    printWindow.document.close();

    setTimeout(function () {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (_e) { }
    }, 300);
  }

  onCheckRow(row: any): void {
    this.checkRow.emit(row);
  }

  onEditRow(row: any): void {
    this.editRow.emit(row);
  }

  onHistoryRow(row: any): void {
    this.history.emit(row);
  }

  onDeactivateRow(row: any): void {
    this.deactivate.emit(row);
  }
}
